import { create } from "zustand";

/**
 * Voice state, kept out of the main app store so the voice layer can be
 * removed without touching gameplay code. All state here is local-client only;
 * persistence (the muted-players set) lives in localStorage under a fixed key.
 */

export interface VoiceTokenInfo {
  url: string;
  token: string;
  identity: string;
  roomName: string;
}

export type VoiceConnState =
  | "idle"
  | "connecting"
  | "connected"
  | "error"
  | "disabled";

/** localStorage key for the per-player mute set. */
const MUTED_KEYS_LS = "solblox.voice.muted.v1";
/** localStorage key for the PTT preference. */
const PTT_PREF_LS = "solblox.voice.ptt.v1";

function loadMutedKeys(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(MUTED_KEYS_LS);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (Array.isArray(arr)) {
      return new Set(arr.filter((x): x is string => typeof x === "string"));
    }
  } catch {
    /* ignore parse errors */
  }
  return new Set();
}

function saveMutedKeys(s: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MUTED_KEYS_LS, JSON.stringify(Array.from(s)));
  } catch {
    /* quota / private-mode: best effort */
  }
}

function loadPttPref(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(PTT_PREF_LS) === "1";
  } catch {
    return false;
  }
}

function savePttPref(on: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PTT_PREF_LS, on ? "1" : "0");
  } catch {
    /* ignore */
  }
}

interface VoiceStore {
  /** Connection lifecycle state. */
  conn: VoiceConnState;
  /** Error message when conn === "error". */
  error: string | null;
  /** Last token info received from server (null if voice is disabled). */
  tokenInfo: VoiceTokenInfo | null;

  /** Whether the local mic is publishing audio right now. */
  micEnabled: boolean;
  /** null = never asked, true = granted, false = denied. */
  micPermission: boolean | null;

  /** Push-to-talk preference. */
  pttMode: boolean;
  /** True while the PTT key is held down. */
  pttHeld: boolean;

  /** Active speakers, by LiveKit identity (= Colyseus sessionId). */
  activeSpeakers: Set<string>;
  /** All remote participants known to LiveKit, by identity. */
  remoteIdentities: Set<string>;

  /** Persistent mute set, keyed by pubkey when available else sessionId. */
  mutedKeys: Set<string>;

  setConn: (state: VoiceConnState, error?: string | null) => void;
  setTokenInfo: (info: VoiceTokenInfo | null) => void;
  setMicEnabled: (enabled: boolean) => void;
  setMicPermission: (granted: boolean | null) => void;
  setPttMode: (on: boolean) => void;
  setPttHeld: (held: boolean) => void;
  setActiveSpeakers: (ids: Set<string>) => void;
  setRemoteIdentities: (ids: Set<string>) => void;
  togglePlayerMute: (key: string) => void;
  isPlayerMuted: (key: string) => boolean;
}

export const useVoiceStore = create<VoiceStore>((set, get) => ({
  conn: "idle",
  error: null,
  tokenInfo: null,
  micEnabled: false,
  micPermission: null,
  pttMode: loadPttPref(),
  pttHeld: false,
  activeSpeakers: new Set(),
  remoteIdentities: new Set(),
  mutedKeys: loadMutedKeys(),

  setConn: (state, error = null) => set({ conn: state, error }),
  setTokenInfo: (info) => set({ tokenInfo: info }),
  setMicEnabled: (enabled) => set({ micEnabled: enabled }),
  setMicPermission: (granted) => set({ micPermission: granted }),
  setPttMode: (on) => {
    savePttPref(on);
    set({ pttMode: on, pttHeld: false });
  },
  setPttHeld: (held) => set({ pttHeld: held }),
  setActiveSpeakers: (ids) => set({ activeSpeakers: ids }),
  setRemoteIdentities: (ids) => set({ remoteIdentities: ids }),
  togglePlayerMute: (key) => {
    const cur = get().mutedKeys;
    const next = new Set(cur);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    saveMutedKeys(next);
    set({ mutedKeys: next });
  },
  isPlayerMuted: (key) => get().mutedKeys.has(key),
}));

/**
 * Resolve the persistence key for a remote player: pubkey when the wallet is
 * connected (stable across reconnects); sessionId otherwise (ephemeral but
 * still useful within the current room).
 */
export function muteKeyFor(pubkey: string | undefined, sessionId: string): string {
  if (pubkey && pubkey.length > 0) return `pk:${pubkey}`;
  return `sid:${sessionId}`;
}
